import { BackCta, Column, FONT_WEIGHTS, Icon, Link, MEDIA, MediaStack, Row, TagText } from "@bridgesplit/ui";
import { LoopMetadataTag } from "@bridgesplit/abf-sdk";
import { doNothing, formatDate } from "@bridgesplit/utils";
import {
    getLoopComingSoonBanner,
    isLoopComingSoon,
    LoopExpanded,
    MarketGuideMode,
    isPtToken
} from "@bridgesplit/abf-react";
import { COPY, LOOPS_SLUG } from "app/constants";

import { useLoopContext } from "../LoopContext";
import { ComingSoonBanner, ComingSoonTag, LoopName, OpenHowItWorks } from "../../common";

export default function LoopDetailHeader() {
    const { loopExpanded } = useLoopContext();
    const comingSoon = isLoopComingSoon(loopExpanded);

    return (
        <Column spacing={2}>
            <Link to={LOOPS_SLUG}>
                <BackCta back={doNothing} backCta={`All ${COPY.LOOP_TERM_PLURAL}`} textVariant="body2" />
            </Link>

            <Row spaceBetween alignItems={"flex-start"}>
                <MediaStack query={MEDIA.SM} spacing={2} sxAbove={{ alignItems: "flex-start" }}>
                    <LoopName loopExpanded={loopExpanded} variant="h1" />
                    <LoopTags loopExpanded={loopExpanded} />
                </MediaStack>
                {loopExpanded && !comingSoon && (
                    <OpenHowItWorks
                        variant="text"
                        mode={MarketGuideMode.Earn}
                        loopExpanded={loopExpanded}
                        textProps={{ sx: { fontWeight: FONT_WEIGHTS.normal, color: "text.secondary" } }}
                        sx={{ [MEDIA.SM.below]: { mx: 0 } }}
                    />
                )}
            </Row>

            {comingSoon && <ComingSoonBanner text={getLoopComingSoonBanner(loopExpanded)} />}
        </Column>
    );
}

function LoopTags({ loopExpanded }: { loopExpanded: LoopExpanded | undefined }) {
    if (!loopExpanded) return null;
    const comingSoon = isLoopComingSoon(loopExpanded);
    const baseTags = loopExpanded.loopVault.metadataTags ?? [];
    const primaryTag = baseTags.includes(LoopMetadataTag.New)
        ? "New"
        : loopExpanded.loopVault.featured
        ? "Featured"
        : undefined;
    const additionalTags = [
        ...(baseTags.includes(LoopMetadataTag.Leverage) ? ["Leverage"] : []),
        ...(loopExpanded.loopVault.protocols ?? [])
    ];
    const showMaturity = isPtToken(loopExpanded.collateralToken) && loopExpanded.loopVault.maturityTimestamp;
    if (!comingSoon && !primaryTag && additionalTags.length === 0 && !showMaturity) return null;

    return (
        <Row spacing={1} wrap>
            {comingSoon && <ComingSoonTag />}
            {!comingSoon && primaryTag && <LoopTag text={primaryTag} variant="primary" />}
            {additionalTags.map((tag, index) => (
                <LoopTag key={index} text={tag} />
            ))}
            {showMaturity && (
                <LoopTag
                    text={formatDate(loopExpanded.loopVault.maturityTimestamp, "date", {
                        alwaysShowYear: true,
                        showTime: "never"
                    })}
                    tooltip={`${loopExpanded.collateralToken.symbol} can be redeemed for its full value on this maturity date.`}
                />
            )}
        </Row>
    );
}

function LoopTag({
    text,
    tooltip,
    variant = "default"
}: {
    text: string;
    tooltip?: string;
    variant?: "primary" | "default";
}) {
    return (
        <TagText
            variant="colored"
            color={variant === "primary" ? "secondary" : undefined}
            textVariant="body2"
            px={1.5}
            py={0.5}
            borderRadius={20}
            useTooltip={!!tooltip}
            helpText={tooltip}
        >
            {text}
            {tooltip && <Icon type="tooltip" />}
        </TagText>
    );
}
